import { PageContainer, ProTable } from '@ant-design/pro-components';
import { useMemo, useRef } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import { queryAgentRuns, queryAgents, queryAvailableAgents } from '../services/agents';
import { formatAgentRunSummary } from '../utils/agent-runs';
import AgentConfigDrawer from './AgentConfigDrawer';
import AgentScheduleTable from './AgentScheduleTable';

const RUN_STATUS = {
  CANCELLED: { text: '已取消', status: 'Default' },
  DELAYED: { text: '已延迟', status: 'Warning' },
  ENQUEUED: { text: '排队中', status: 'Processing' },
  ERROR: { text: '失败', status: 'Error' },
  MAX_RECOVERY_ATTEMPTS_EXCEEDED: { text: '恢复失败', status: 'Error' },
  PENDING: { text: '运行中', status: 'Processing' },
  SUCCESS: { text: '成功', status: 'Success' },
};

const TRIGGER_TYPE = {
  manual: { text: '手动', status: 'Processing' },
  schedule: { text: '定时', status: 'Default' },
};

const agentTabList = [
  { key: 'agents', tab: '智能体' },
  { key: 'schedules', tab: '定时任务' },
  { key: 'runs', tab: '运行记录' },
];

const getAgentTabKey = (query) => {
  const tab = Array.isArray(query.tab) ? query.tab.at(-1) : query.tab;
  return agentTabList.some((item) => item.key === tab) ? tab : agentTabList[0].key;
};

const getRunInput = (run) => (Array.isArray(run.input) ? run.input[0] : run.input) || {};

const Agents = () => {
  const runActionRef = useRef(null);
  const { organizationId } = useOrganizationPath();
  const [query, setQuery] = useQueryParams();
  const activeTabKey = getAgentTabKey(query);

  const agentColumns = useMemo(
    () => [
      { title: '智能体', dataIndex: 'title' },
      { title: 'Agent ID', dataIndex: 'agentId', hideInTable: true, search: false },
      { title: '说明', dataIndex: 'description', search: false },
      {
        title: '操作',
        valueType: 'option',
        render: (_, record) => [
          <AgentConfigDrawer
            key="config"
            agent={record}
            organizationId={organizationId}
            onRun={() => runActionRef.current?.reload?.()}
          />,
        ],
      },
    ],
    [organizationId],
  );

  const runColumns = [
    {
      title: '智能体',
      dataIndex: 'agentId',
      valueType: 'select',
      render: (_, record) => record.agentTitle || record.agentId,
      fieldProps: { fieldNames: { label: 'title', value: 'agentId' } },
      request: () => queryAvailableAgents({ organizationId }),
    },
    {
      title: '采集配置',
      dataIndex: 'collectionName',
      renderText: (_, record) => getRunInput(record).collectionName || '-',
      search: false,
    },
    {
      title: '触发方式',
      dataIndex: 'triggerType',
      valueEnum: TRIGGER_TYPE,
      renderText: (_, record) => getRunInput(record).triggerType || '-',
      search: false,
    },
    {
      title: '定时任务 ID',
      dataIndex: 'scheduleId',
      renderText: (_, record) => getRunInput(record).scheduleId || '-',
      search: false,
      ellipsis: true,
    },
    { title: '状态', dataIndex: 'status', valueEnum: RUN_STATUS },
    {
      title: '汇总',
      dataIndex: 'summary',
      renderText: formatAgentRunSummary,
      search: false,
    },
    { title: '创建时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
    { title: '更新时间', dataIndex: 'updatedAt', valueType: 'dateTime', search: false },
  ];

  return (
    <PageContainer
      content="组织管理员在这里查看智能体、配置定时任务，并查看运行记录。"
      tabList={agentTabList}
      tabActiveKey={activeTabKey}
      onTabChange={(key) => setQuery({ tab: key })}
    >
      {activeTabKey === 'agents' ? (
        <ProTable
          rowKey="agentId"
          headerTitle="智能体"
          request={(params) => queryAgents({ ...params, organizationId })}
          columns={agentColumns}
          search={{ labelWidth: 96 }}
        />
      ) : null}
      {activeTabKey === 'schedules' ? (
        <AgentScheduleTable
          organizationId={organizationId}
          onRun={() => runActionRef.current?.reload?.()}
        />
      ) : null}
      {activeTabKey === 'runs' ? (
        <ProTable
          actionRef={runActionRef}
          rowKey="workflowId"
          headerTitle="运行记录"
          request={(params) => queryAgentRuns({ ...params, organizationId })}
          columns={runColumns}
          search={{ labelWidth: 96 }}
        />
      ) : null}
    </PageContainer>
  );
};

export default Agents;
